/**
 * ApiContext
 * Now that urls are encoded into environmental variables, we can remove this ApiContext.
 */
import React, { useMemo, createContext, useContext, useEffect, useCallback } from "react";
import { AxiosInstance } from "axios";
import { logout, setErrorInterceptors } from "@/services/authService";
import { useRouter } from "next/router";
import { apiClient } from "@/libs/axios";

import { HTTP_ERRORS_BY_STATUS_CODE } from "@/constants";
import { HttpError, UnauthorizedError } from "@/utils/error";

/** ApiContext stores a singular http Api instance (AxiosInstance) */
const ApiContext = createContext<{
  instance: AxiosInstance;
  clearSession(api: AxiosInstance): void;
} | null>(null);

export function ApiProvider({ children }: { children: React.ReactElement }) {
  const router = useRouter();
  const apic = apiClient();

  const clearSession = useCallback(async (api: AxiosInstance) => {
    await logout(false);
  }, []);

  // Initialize our Axios interceptors
  useEffect(() => {
    async function run() {
      // TODO: ADD REFRESH TOKENS?
      setErrorInterceptors(apic, (statusCode) => {
        switch (statusCode) {
          case 401:
            clearSession(apic);
            throw new UnauthorizedError();
          default:
            const AppropriateHttpError = HTTP_ERRORS_BY_STATUS_CODE[statusCode] ?? HttpError;
            throw new AppropriateHttpError();
        }
      });
    }

    run();
  }, [apic, router, clearSession]);

  const api = useMemo(() => {
    return {
      instance: apic,
      clearSession,
    };
  }, [apic, clearSession]);

  return <ApiContext.Provider value={api}>{children}</ApiContext.Provider>;
}

/** useApiContext returns the Api instance */
export function useApiContext() {
  const context = useContext(ApiContext);
  if (!context) {
    throw new Error("Please wrap your app in an ApiProvider");
  }
  return context;
}
